import * as React from 'react';
import { Instance } from 'mobx-state-tree';
import styled from '@emotion/styled';
import { inject, observer } from 'mobx-react';
import DutiesSearchForm from './DutiesSearchForm';
import DutiesSearchResult from './DutiesSearchResult';
import MainSectionLayout from '../layout/MainSectionLayout';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import ShowDutiesOnWideMap from './ShowDutiesOnWideMap';
import DutiesListActions from './DutiesListActions';
import DutyCalendar from './DutyCalendar';
import { Card } from '@blueprintjs/core';
import { css } from 'emotion';
import {
  AnchorStore,
  AuthenticationStore,
  DutyAssignmentStore,
  DutyStore,
  DutyTypeStore,
  BlackPointStore,
  DutyOffRequestStore,
  PharmacyDetailedStore,
  PharmacyStore,
  PharmacyStatusStore,
  SecretariatNeighborStore,
  UiStore,
  GroupStore,
  SecretariatStore,
} from '../../store';
import moment from 'moment';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { generateBasicIcon, generateCustomIcon } from '../../common/icons';
import { PHARMACY_STATUS_AC, PHARMACY_STATUS_TC } from '../../common/constants/pharmacyStatusConstants';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const SearchFormContainer = styled.div`
  flex: 0;
  width: 100%;
`;

const SearchResultContainer = styled.div`
  flex: 1;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex: 0;
  width: 100%;
  justify-content: center;
  margin-top: 5px;
`;

const CardStyle = css({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const DivLongLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const DivColumn450px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 450px;
`;

const DivColumn350px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 350px;
`;

const DivColumn250px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 250px;
`;

const DivColumn175px = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 150px;
`;

const DivColumn60 = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 60%;
`;

const DivColumn80 = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 2px;
  width: 80%;
`;

interface DutiesPageProps {
  DutyAssignmentStore: Instance<typeof DutyAssignmentStore>;
  PharmacyDetailedStore: Instance<typeof PharmacyDetailedStore>;
  PharmacyStore: Instance<typeof PharmacyStore>;
  PharmacyStatusStore: Instance<typeof PharmacyStatusStore>;
  DutyTypeStore: Instance<typeof DutyTypeStore>;
  DutyStore: Instance<typeof DutyStore>;
  BlackPointStore: Instance<typeof BlackPointStore>;
  DutyOffRequestStore: Instance<typeof DutyOffRequestStore>;
  AnchorStore: Instance<typeof AnchorStore>;
  AuthenticationStore: Instance<typeof AuthenticationStore>;
  SecretariatNeighborStore: Instance<typeof SecretariatNeighborStore>;
  UiStore: Instance<typeof UiStore>;
  GroupStore: Instance<typeof GroupStore>;
  SecretariatStore: Instance<typeof SecretariatStore>;
}

interface DutiesPageState {
  dutiesGrid: Array<Instance<typeof DutyAssignment>>;
  view: 'grid' | 'calendar' | 'wideMap';
  startDate?: string;
  endDate?: string;
  isLoading: boolean;
  dutySelected?: Instance<typeof DutyAssignment>;
  minimalDutiesPage: boolean;
  pharmacyId?: number;
}

@inject('AnchorStore')
@inject('AuthenticationStore')
@inject('DutyAssignmentStore')
@inject('SecretariatNeighborStore')
@inject('PharmacyDetailedStore')
@inject('PharmacyStore')
@inject('PharmacyStatusStore')
@inject('DutyStore')
@inject('BlackPointStore')
@inject('DutyOffRequestStore')
@inject('DutyTypeStore')
@inject('UiStore')
@inject('GroupStore', 'SecretariatStore')
@observer
class DutiesPage extends React.Component<DutiesPageProps & WithTranslateProps, DutiesPageState> {
  constructor(props: DutiesPageProps & WithTranslateProps) {
    super(props);
    this.state = {
      isLoading: true,
      dutiesGrid: [],
      view: 'wideMap',
      startDate: moment().format(),
      endDate: moment().format(),
      minimalDutiesPage: true,
      pharmacyId: undefined,
    };
  }

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    await Promise.all([
      this.props.DutyTypeStore.checkListLoaded(),
      this.props.PharmacyStore.checkListLoaded(),
      this.props.PharmacyStatusStore.checkListLoaded(),
      this.props.AnchorStore.checkListLoaded(),
      this.props.GroupStore.checkListLoaded(),
      this.props.SecretariatStore.checkListLoaded(),
      this.props.SecretariatNeighborStore!.loadList(),
    ]);
    this._isMounted &&
      this.setState({
        isLoading: false,
        minimalDutiesPage: this.isMinimalDutiesPage(),
        pharmacyId: this.getPharmacyId(),
      });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  private isMinimalDutiesPage = (): boolean => {
    if (
      this.props.AuthenticationStore!.isAdmin ||
      this.props.AuthenticationStore!.isSuperAdmin ||
      this.props.AuthenticationStore!.isSecretariat
    )
      return false;
    return true;
  };

  private getPharmacyId = (): number | undefined => {
    if (this.props.AuthenticationStore!.isPharmacy)
      return this.props.AuthenticationStore!.pharmacyId === null
        ? undefined
        : this.props.AuthenticationStore!.pharmacyId;
    return undefined;
  };

  searchDuties = async (criteria: any) => {
    let duties: any[] = [];
    try {
      const responses = await Promise.all([
        this.props.DutyAssignmentStore.searchAssignmentForPharmacies(criteria),
        this.props.DutyStore.searchForDutyAssignmentForPharmacies(criteria),
      ]);
      duties = responses[0]!;
    } catch (e) {
      console.error('Error while searching for duties:', e);
    }
    return duties;
  };

  searchBlackpoints = async (criteria: any) => {
    let bps = [];
    await Promise.all([this.props.BlackPointStore.search(criteria)])
      .catch((e) => console.error(e))
      .then((responses) => (bps = responses[0]));
    return bps;
  };

  searchDutyOffs = async (criteria: any) => {
    let bps = [];
    await Promise.all([this.props.DutyOffRequestStore.searchDutyOffRequest(criteria)])
      .catch((e) => console.error(e))
      .then((responses) => (bps = responses[0]));
    return bps;
  };

  searchGrid = async (criteria: any) => {
    const duties = await this.searchDuties(criteria);
    this._isMounted && this.setState({ dutiesGrid: duties });
  };

  searchMap = async (criteria: any) => {
    const duties = await this.searchDuties(criteria);
    return duties;
  };

  searchBpts = async (criteria: any) => {
    const bp = await this.searchBlackpoints(criteria);
    return bp;
  };

  searchDtos = async (criteria: any) => {
    const dOff = await this.searchDutyOffs(criteria);
    return dOff;
  };

  onStartDateChange = async (date?: string) => {
    if (date !== this.state.startDate) {
      this._isMounted && this.setState({ startDate: date });
    }
  };

  onMapDateChange = async (date?: string) => {
    this._isMounted && this.setState({ startDate: date, endDate: date });
    this.props.UiStore!.setShowDutiesOnMapDate(date!);
  };

  onEndDateChange = async (date?: string) => {
    if (date !== this.state.endDate) {
      this._isMounted && this.setState({ endDate: date });
    }
  };

  onListDutySelect = (selection) => {
    const dutyAss = this.state.dutiesGrid.find((elem) => elem.dutyAssignmentId === selection.id);
    this.setState({ dutySelected: dutyAss });
  };

  render() {
    const { dutiesGrid, view } = this.state;
    const { PharmacyDetailedStore, PharmacyStore, PharmacyStatusStore, AuthenticationStore, GroupStore, SecretariatStore } = this.props;

    const activeStatus = PharmacyStatusStore!.dataList.find((s) => s.code == PHARMACY_STATUS_AC);
    const temporaryClosedStatus = PharmacyStatusStore!.dataList.find((s) => s.code == PHARMACY_STATUS_TC);

    var pharmacyList = [] as any[];

    if (activeStatus && temporaryClosedStatus) {
      pharmacyList = PharmacyStore!.dataList.filter((ph) => ph.statusId == activeStatus.pharmacyStatusId || ph.statusId == temporaryClosedStatus.pharmacyStatusId);
    }

    const groupList = GroupStore!.dataList;
    const secretariatList = SecretariatStore!.dataList;

    return (
      <MainSectionLayout isLoading={this.state.isLoading || pharmacyList.length === 0}>
        <PageContainer>
          {pharmacyList.length !== 0 ? (
            <Card className={CardStyle.toString()}>
              {view === 'grid' ? (
                <>
                  <SearchFormContainer>
                    <DutiesSearchForm
                      startDate={this.state.startDate}
                      endDate={this.state.endDate}
                      formSubmitted={this.searchGrid}
                      onStartDateChange={this.onStartDateChange}
                      onEndDateChange={this.onEndDateChange}
                      getPharmacyDetails={PharmacyDetailedStore!.loadElement}
                      pharmacyList={pharmacyList}
                      getPharmacyById={PharmacyStore!.getById}
                    />
                  </SearchFormContainer>
                  <SearchResultContainer>
                    <DutiesSearchResult
                      duties={dutiesGrid}
                      onListDutySelect={this.onListDutySelect}
                      groups={groupList}
                      secretariats={secretariatList}
                    />
                  </SearchResultContainer>
                  <ButtonContainer>
                    <DutiesListActions onShowOnMap={() => this.setState({ view: 'wideMap' })} />
                  </ButtonContainer>
                </>
              ) : view === 'wideMap' ? (
                <>
                  <ShowDutiesOnWideMap
                    searchDuties={this.searchMap}
                    searchBlackpoints={this.searchBpts}
                    searchDutyOffs={this.searchDtos}
                    pharmacyList={pharmacyList}
                    getPharmacyById={PharmacyStore!.getById}
                    getPharmacyDetails={PharmacyDetailedStore!.loadElement}
                    date={
                      this.props.UiStore!.showDutiesOnMapDate
                        ? this.props.UiStore!.showDutiesOnMapDate
                        : this.state.startDate!.toString()
                    }
                    anchors={this.props.AnchorStore!.dataList}
                    dutySelected={this.state.dutySelected}
                    onDateChange={this.onMapDateChange}
                    isSecretariat={AuthenticationStore!.isSecretariat || AuthenticationStore!.isAdmin}
                    groupEditMode={false}
                    planningid={this.props.UiStore!.diagnosticPageCurrentPlanningId}
                    groupList={groupList}
                    minimalDutiesPage={this.state.minimalDutiesPage}
                    pharmacyId={this.state.pharmacyId}
                    onListButtonClicked={() => this.setState({ view: 'grid' })}
                  />
                  <DivLongLine>
                    {this.state.minimalDutiesPage ? (
                      <DivColumn60>
                        <DivLongLine>
                          <DivColumn175px>
                            <label className="bp3-label .modifier">
                              garde jour (9 à 22h)&nbsp;
                              <img
                                src={
                                  generateCustomIcon(
                                    24,
                                    24,
                                    'FFCA18',
                                    'FFCA18',
                                    false,
                                    '000000',
                                    false,
                                    '888888',
                                    '1',
                                    '1',
                                  ).url
                                }
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn175px>
                          <DivColumn175px>
                            <label className="bp3-label .modifier">
                              garde nuit&nbsp;
                              <img
                                src={
                                  generateCustomIcon(
                                    24,
                                    24,
                                    'FFCA18',
                                    '888888',
                                    false,
                                    '000000',
                                    false,
                                    '888888',
                                    '1',
                                    '1',
                                  ).url
                                }
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn175px>
                          <DivColumn250px>
                            <label className="bp3-label .modifier">
                              Ma pharmacie (pas de garde)&nbsp;
                              <img
                                src={
                                  generateCustomIcon(
                                    32,
                                    32,
                                    '29B6F6',
                                    '29B6F6',
                                    true,
                                    '000000',
                                    false,
                                    '888888',
                                    '1',
                                    '1',
                                  ).url /*lightblue*/
                                }
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn250px>
                        </DivLongLine>
                      </DivColumn60>
                    ) : (
                      <DivColumn80>
                        <DivLongLine>
                          <DivColumn175px>
                            <label className="bp3-label .modifier">
                              garde niv.0&nbsp;
                              <img
                                src={generateBasicIcon(0, false, false, false, false).url /*mm20Gray*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn175px>
                          <DivColumn175px>
                            <label className="bp3-label .modifier">
                              garde niv.1&nbsp;
                              <img
                                src={generateBasicIcon(1, false, false, false, false).url /*yellow*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn175px>
                          <DivColumn175px>
                            <label className="bp3-label .modifier">
                              garde niv.3&nbsp;
                              <img
                                src={generateBasicIcon(3, false, false, false, false).url /*yellowDot*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn175px>
                          <DivColumn250px>
                            <label className="bp3-label .modifier">
                              phie sélect. de garde&nbsp;
                              <img
                                src={generateBasicIcon(3, false, true, false, false).url /*blue*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn250px>
                          <DivColumn250px>
                            <label className="bp3-label .modifier">
                              phie sélect. libre&nbsp;
                              <img
                                src={generateBasicIcon(3, false, false, true, false).url /*lightblue*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn250px>
                          <DivColumn350px>
                            <label className="bp3-label .modifier">
                              garde en zone d'exclusion&nbsp;
                              <img
                                src={generateBasicIcon(1, false, false, false, true).url /*red*/}
                                width="16"
                                height="16"
                              />
                              <img
                                src={generateBasicIcon(3, false, false, false, true).url /*redDot*/}
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn350px>
                          <DivColumn450px>
                            <label className="bp3-label .modifier">
                              phie avec exemption, BP ou preff duty off&nbsp;
                              <img
                                src={
                                  generateCustomIcon(16, 16, 'ff0000', 'ff0000', true, '000000', false, '', '1', '1')
                                    .url /*redDot*/
                                }
                                width="16"
                                height="16"
                              />
                            </label>
                          </DivColumn450px>
                        </DivLongLine>
                      </DivColumn80>
                    )}
                  </DivLongLine>
                </>
              ) : (
                <>
                  <DutyCalendar />
                </>
              )}
            </Card>
          ) : (
            <Card />
          )}
        </PageContainer>
      </MainSectionLayout>
    );
  }
}

export default withTranslate(DutiesPage);
